import { MaterialCommunityIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Box, Button, HStack, Icon, Pressable, ScrollView, Text, useTheme } from "native-base";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, ScrollView as RNScrollView } from "react-native";

import { Routes } from "../../constants";

const { width: viewportWidth } = Dimensions.get("window");

interface PostOnboardingScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

type SlideItem = {
  title: string;
  description: string;
  icon: string;
};

const PostOnboardingScreen: React.FC<PostOnboardingScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const scrollViewRef = useRef<RNScrollView>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const slides: SlideItem[] = [
    {
      title: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_1_title"),
      description: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_1_description"),
      icon: "check-circle-outline",
    },
    {
      title: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_2_title"),
      description: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_2_description"),
      icon: "food-variant",
    },
    {
      title: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_3_title"),
      description: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_3_description"),
      icon: "cart-outline",
    },
    {
      title: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_4_title"),
      description: t("onboarding.onboarding_11_post_onboarding_screen.carousel_slide_4_description"),
      icon: "chef-hat",
    },
  ];

  const handleScroll = (event: { nativeEvent: { contentOffset: { x: number } } }): void => {
    const slideIndex = Math.floor(event.nativeEvent.contentOffset.x / viewportWidth);
    setActiveSlide(slideIndex);
  };

  const goToNextSlide = (): void => {
    if (activeSlide < slides.length - 1) {
      scrollViewRef.current?.scrollTo({
        x: viewportWidth * (activeSlide + 1),
        animated: true,
      });
    }
  };

  const goToPreviousSlide = (): void => {
    if (activeSlide > 0) {
      scrollViewRef.current?.scrollTo({
        x: viewportWidth * (activeSlide - 1),
        animated: true,
      });
    }
  };

  const renderSlide = (item: SlideItem, index: number): JSX.Element => (
    <Box key={index} width={viewportWidth} height="100%" justifyContent="center" alignItems="center" px={12}>
      <Icon as={MaterialCommunityIcons} name={item.icon} size={10} color="primary.50" />
      <Text fontSize="2xl" fontWeight="semibold" mt={4} color="function.50" textAlign="center">
        {item.title}
      </Text>
      <Text fontSize="md" color="function.30" textAlign="center" mt={2} maxWidth="90%">
        {item.description}
      </Text>
    </Box>
  );

  const renderPagination = (): JSX.Element => (
    <HStack space={2} alignItems="center" justifyContent="center" mt={4} mb={5}>
      {slides.map((_, index) => (
        <Box
          key={index}
          width={2.5}
          height={2.5}
          borderRadius="full"
          bg={activeSlide === index ? "primary.50" : "function.20"}
        />
      ))}
    </HStack>
  );

  const handleProceed = (): void => {
    if (activeSlide === slides.length - 1) {
      navigation.navigate(Routes.BottomNavigationTab, { screen: Routes.DiaryViewScreen });
    } else {
      goToNextSlide();
    }
  };

  const getButtonTitle = (): string => {
    if (activeSlide === 0) {
      return t("onboarding.onboarding_11_post_onboarding_screen.start_tour");
    }
    if (activeSlide === slides.length - 1) {
      return t("onboarding.onboarding_11_post_onboarding_screen.proceed_button");
    }
    return t("general.next_button_text");
  };

  return (
    <Box flex={1} bg="primary.10" width={viewportWidth}>
      <Box flex={1} justifyContent="center" alignItems="center" position="relative">
        <ScrollView
          ref={scrollViewRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          _contentContainerStyle={{
            width: `${100 * slides.length}%`,
            alignItems: "center",
          }}
          width={viewportWidth}
          height="60%"
        >
          {slides.map((item, index) => renderSlide(item, index))}
        </ScrollView>
      </Box>

      <Box position="absolute" bottom={0} left={0} right={0} alignItems="center">
        {renderPagination()}

        <Button
          onPress={handleProceed}
          testID="proceed-button"
          bg="primary.50"
          _pressed={{ bg: "primary.50:alpha.80" }}
          size="lg"
          width="60%"
          borderRadius={theme.borderRadii.button.default}
          shadow={2}
          mb={10}
          mt={4}
          _text={{ fontWeight: "semibold", color: "primary.10" }}
        >
          {getButtonTitle()}
        </Button>
      </Box>

      {activeSlide > 0 && (
        <Pressable position="absolute" top="50%" left={4} onPress={goToPreviousSlide} _pressed={{ opacity: 0.7 }}>
          <Text fontSize="3xl" color="function.30">
            ‹
          </Text>
        </Pressable>
      )}
      {activeSlide < slides.length - 1 && (
        <Pressable position="absolute" top="50%" right={4} onPress={goToNextSlide} _pressed={{ opacity: 0.7 }}>
          <Text fontSize="3xl" color="function.30">
            ›
          </Text>
        </Pressable>
      )}
    </Box>
  );
};

export default PostOnboardingScreen;
